<script setup>
import { useForm } from '@inertiajs/vue3';
import { computed, getCurrentInstance } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Card from 'primevue/card';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const selectIcon = (value) => {
    form.icon_name = value;
};

const form = useForm({
    name: '',
    icon_name: null,
});

const iconOptions = [
    { label: 'Dice (Casinos)', value: 'pi pi-bitcoin', icon: 'pi pi-bitcoin' },
    { label: 'Heart (Dating)', value: 'pi pi-heart', icon: 'pi pi-heart' },
    { label: 'Shield (Crypto)', value: 'pi pi-shield', icon: 'pi pi-shield' },
    { label: 'Bolt (Bet)', value: 'pi pi-bolt', icon: 'pi pi-bolt' },
    { label: 'Ban (Erotic)', value: 'pi pi-ban', icon: 'pi pi-ban' },
    { label: 'Wallet (Loan)', value: 'pi pi-wallet', icon: 'pi pi-wallet' },
    { label: 'Chart Line (Forex)', value: 'pi pi-chart-line', icon: 'pi pi-chart-line' },
    { label: 'Globe', value: 'pi pi-globe', icon: 'pi pi-globe' },
    { label: 'Star', value: 'pi pi-star', icon: 'pi pi-star' },
    { label: 'Tag', value: 'pi pi-tag', icon: 'pi pi-tag' },
    { label: 'Shopping Cart', value: 'pi pi-shopping-cart', icon: 'pi pi-shopping-cart' },
    { label: 'Briefcase', value: 'pi pi-briefcase', icon: 'pi pi-briefcase' },
    { label: 'Building', value: 'pi pi-building', icon: 'pi pi-building' },
    { label: 'Car', value: 'pi pi-car', icon: 'pi pi-car' },
    { label: 'Graduation Cap', value: 'pi pi-graduation-cap', icon: 'pi pi-graduation-cap' },
    { label: 'Wrench', value: 'pi pi-wrench', icon: 'pi pi-wrench' },
    { label: 'Microphone', value: 'pi pi-microphone', icon: 'pi pi-microphone' },
    { label: 'Camera', value: 'pi pi-camera', icon: 'pi pi-camera' },
    { label: 'Palette', value: 'pi pi-palette', icon: 'pi pi-palette' },
    { label: 'Flag', value: 'pi pi-flag', icon: 'pi pi-flag' },
    { label: 'Trophy', value: 'pi pi-trophy', icon: 'pi pi-trophy' },
    { label: 'Sparkles', value: 'pi pi-sparkles', icon: 'pi pi-sparkles' },
    { label: 'Megaphone', value: 'pi pi-megaphone', icon: 'pi pi-megaphone' },
    { label: 'Hashtag', value: 'pi pi-hashtag', icon: 'pi pi-hashtag' },
    { label: 'Crown', value: 'pi pi-crown', icon: 'pi pi-crown' },
    { label: 'Headphones', value: 'pi pi-headphones', icon: 'pi pi-headphones' },
];

const submit = () => {
    form.post(route('types.store'), {
        onStart: () => showLoading('Creating type...'),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Type created successfully',
                life: 3000
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to create type',
                life: 5000
            });
        }
    });
};
</script>

<template>
    <AdminMainLayout>
        <div class="grid gap-6">
            <div class="col-12 lg:col-8">
                <Card>
                    <template #title>
                        <div class="flex align-items-center gap-2">
                            <i class="pi pi-plus text-2xl"></i>
                            <span>{{ $t('Add Niche') }}</span>
                        </div>
                    </template>

                <template #content>
                    <form @submit.prevent="submit">
                        <div class="formgrid grid">
                            <div class="field col-12">
                                <label for="name" class="font-semibold">{{ $t('Name') }} <span class="text-red-500">*</span></label>
                                <InputText
                                    id="name"
                                    v-model="form.name"
                                    class="w-full"
                                    :class="{ 'p-invalid': form.errors.name }"
                                    :placeholder="$t('Enter niche name')"
                                    autocomplete="name"
                                    :disabled="form.processing" />
                                <small v-if="form.errors.name" class="p-error text-red-500 font-medium">{{ $t(form.errors.name) }}</small>
                            </div>

                            <div class="field col-12">
                                <label class="font-semibold">{{ $t('Icon') }} <span class="text-red-500">*</span></label>
                                <div class="mt-2 border border-surface-200 rounded-lg p-3">
                                    <div class="flex flex-wrap gap-1">
                                        <button
                                            v-for="opt in iconOptions"
                                            :key="opt.value"
                                            type="button"
                                            v-tooltip.top="opt.label"
                                            @click="selectIcon(opt.value)"
                                            :class="['w-10 h-10 rounded-lg flex items-center justify-center transition-colors', form.icon_name === opt.value ? 'bg-primary-100 text-primary-600 ring-2 ring-primary-400' : 'hover:bg-surface-100 text-surface-700']">
                                            <i :class="opt.icon" class="text-lg" />
                                        </button>
                                    </div>
                                </div>
                                <small v-if="form.errors.icon_name" class="p-error text-red-500 font-medium">{{ form.errors.icon_name }}</small>
                            </div>
                        </div>

                        <div class="flex gap-2 mt-4">
                            <Button
                                type="submit"
                                :label="$t('Save')"
                                icon="pi pi-check"
                                :loading="form.processing"
                                :disabled="form.processing" />

                            <Button
                                :label="$t('Cancel')"
                                icon="pi pi-times"
                                class="p-button-secondary"
                                @click="() => { showLoading('Loading types...'); $inertia.visit(route('types.index'), { onFinish: () => hideLoading() }); }"
                                :disabled="form.processing" />
                        </div>

                        <Message v-if="form.recentlySuccessful" severity="success" :closable="false" class="mt-3">
                            {{ $t('Type saved successfully.') }}
                        </Message>
                    </form>
                    </template>
                </Card>
            </div>

            <div class="col-12 lg:col-4">
                <Card>
                    <template #title>
                        <div class="flex align-items-center gap-2">
                            <i class="pi pi-info-circle"></i>
                            <span>{{ $t('Information') }}</span>
                        </div>
                    </template>
                    <template #content>
                        <p class="m-0">
                            {{ $t('Niches help categorize your sites by their characteristics. Enter a descriptive name for the niche that clearly identifies its purpose.') }}
                        </p>
                        <div class="mt-3">
                            <small class="text-gray-600">
                                <strong>{{ $t('Tips:') }}</strong>
                                <ul class="mt-2 pl-4">
                                    <li>{{ $t('Use clear, descriptive names') }}</li>
                                    <li>{{ $t('Keep names concise') }}</li>
                                    <li>{{ $t('Avoid special characters') }}</li>
                                </ul>
                            </small>
                        </div>
                    </template>
                </Card>
            </div>
        </div>
    </AdminMainLayout>
</template>

<style scoped>
.field {
    margin-bottom: 1.5rem;
}

.field label {
    display: block;
    margin-bottom: 0.5rem;
}

.p-error {
    color: #ef4444;
    font-size: 0.875rem;
    margin-top: 0.25rem;
    display: block;
}

:deep(#icon_name.p-select) {
    height: 42px;
}

:deep(#icon_name .p-select-label) {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
}
</style>